/*
 * Pricing page content (Figma "Pricing").
 * Personal compares two plans (Standard / Pro); Business is a single plan.
 * Each row carries one `PriceValue` per plan (aligned with `plans`).
 * `free` / `highlight` render the value in red; `down` prefixes a ↓ arrow;
 * `info` attaches a tooltip (shown via the small info icon).
 */

export interface PriceValue {
  text: string;
  /** Render in red — used for the "Free" values. */
  free?: boolean;
  /** Render in red — used to emphasise a better Pro price. */
  highlight?: boolean;
  /** Prefix a downward arrow (e.g. a reduced fee). */
  down?: boolean;
  /** Tooltip copy; presence renders the info icon. */
  info?: string;
  href?: string;
}

export interface PricingRow {
  label: string;
  /** One value per plan, in the same order as `PricingContent.plans`. */
  values: PriceValue[];
  /**
   * Card pricing. Marked structurally rather than matched on the label, because
   * the label is translated and the Arabic table has to hide the same rows.
   * Dropped from the rendered table while `SHOW_CARD_PRICING` is false.
   */
  cards?: boolean;
}

export interface PricingGroup {
  title: string;
  rows: PricingRow[];
  /** See `PricingRow.cards` — hides the whole group. */
  cards?: boolean;
}

export interface PricingPlan {
  name: string;
  price: string;
  /**
   * Suffix rendered straight after `price` ("$0.00" + "/m"). It is the rendered
   * string, not a description of one, so a translated table shows its own
   * cadence instead of an English "/m".
   */
  cadence: string;
}

export interface PricingContent {
  plans: PricingPlan[];
  groups: PricingGroup[];
}

export type Audience = 'personal' | 'business';

const free: PriceValue = { text: 'Free', free: true };

const personal: PricingContent = {
  plans: [
    { name: 'Standard', price: '$0.00', cadence: '/m' },
    { name: 'Pro', price: '$9.99', cadence: '/m' },
  ],
  groups: [
    {
      title: 'Account',
      rows: [
        { label: 'USD Account', values: [free, free] },
        { label: 'USD ACH Details (Routing & Account number)', values: [free, free] },
        { label: 'USD Wire Setup (IBAN)', values: [{ text: '$5.99' }, free] },
        { label: 'GBP Account Activation', values: [{ text: '$5.99' }, free] },
        { label: 'EUR Account Activation', values: [{ text: '$5.99' }, free] },
        { label: 'USD Wire/GBP/EUR Account Fee', values: [{ text: '$0.99/m' }, free] },
      ],
    },
    {
      title: 'Receive',
      rows: [
        {
          label: 'Receive money in USD (SWIFT/ACH)',
          values: [
            { text: 'Free', free: true, info: 'No fee to receive USD via SWIFT or ACH.' },
            { text: 'Free', free: true, info: 'No fee to receive USD via SWIFT or ACH.' },
          ],
        },
        { label: 'Deposit via Stablecoin', values: [free, free] },
      ],
    },
    {
      title: 'Send',
      rows: [
        { label: 'nsave to nsave transfers', values: [free, free] },
        {
          label: 'Withdraw to your local currency',
          values: [
            {
              text: "Check today's rate",
              href: '/send-money-home',
              info: 'Fees vary by amount and currency.',
            },
            {
              text: "Check today's rate",
              href: '/send-money-home',
              info: 'Fees vary by amount and currency.',
            },
          ],
        },
        { label: 'Send USD internationally (ACH)', values: [{ text: '$0.5' }, { text: '$0.5' }] },
        {
          label: 'Send USD internationally (SWIFT)',
          values: [{ text: '$12.99' }, { text: '$9.99', highlight: true }],
        },
        { label: 'Send GBP (FPS)', values: [{ text: '$1' }, { text: '$1' }] },
        { label: 'Send EUR (SEPA)', values: [{ text: '$1' }, { text: '$1' }] },
        { label: 'Send to a stablecoin wallet', values: [{ text: '1%' }, { text: '1%' }] },
      ],
    },
    {
      title: 'Send to Egypt',
      rows: [
        {
          label: 'Send USD to an Egyptian bank account',
          values: [{ text: '$3.50' }, { text: '$3.50' }],
        },
      ],
    },
    {
      title: 'Cards',
      cards: true,
      rows: [
        {
          label: 'One-time payment to get the Platinum card',
          values: [{ text: '$1.99' }, free],
        },
        {
          label: 'One-time payment to get the Infinite card',
          values: [{ text: '$99.99' }, { text: '$99.99' }],
        },
        { label: 'Card payments in USD', values: [free, free] },
      ],
    },
    {
      title: 'Foreign Exchange Fees',
      rows: [
        {
          label: 'Card payments in a currency other than the currency of your card account',
          values: [{ text: '1.5%' }, { text: '1.5%' }],
          cards: true,
        },
        {
          label: 'Exchange between USD/GBP',
          values: [{ text: '1%' }, { text: '0.5%', down: true }],
        },
      ],
    },
    {
      title: 'Rewards',
      rows: [
        {
          label: 'Yearly rewards on USD balance (Paid out daily)',
          values: [
            { text: '3.2%', info: 'Variable rate, paid out daily.' },
            { text: '4.2%', info: 'Variable rate, paid out daily.' },
          ],
        },
      ],
    },
    {
      title: 'Investment',
      rows: [
        {
          label: 'Fee per order',
          values: [
            { text: '0.2% (min $0.25)', info: 'Charged on each buy or sell order.' },
            { text: '0.2% (min $0.25)', info: 'Charged on each buy or sell order.' },
          ],
        },
      ],
    },
  ],
};

const business: PricingContent = {
  plans: [{ name: 'Standard', price: '$0.00', cadence: '/m' }],
  groups: [
    {
      title: 'Account',
      rows: [
        { label: 'Setup fee', values: [{ text: '$200.00' }] },
        { label: 'USD ACH Details (Routing & Account number)', values: [free] },
        { label: 'USD Wire Setup (IBAN)', values: [free] },
        { label: 'GBP Account Activation', values: [free] },
        { label: 'EUR Account Activation', values: [free] },
        { label: 'FX (GBP ↔ EUR ↔ USD)', values: [{ text: '0.6%' }] },
      ],
    },
    {
      title: 'Send',
      rows: [
        { label: 'Send GBP (FPS)', values: [{ text: '$1' }] },
        {
          label: 'Send USD/EUR (SWIFT)',
          values: [{ text: '0.3% ** (min $14.99, max $24.99)' }],
        },
      ],
    },
    {
      title: 'Rewards',
      rows: [
        {
          label: 'Yearly rewards on USD balance (Coming soon)',
          values: [{ text: '3.2%', info: 'Variable rate, paid out daily.' }],
        },
      ],
    },
  ],
};

/**
 * Card pricing is shown in the public pricing table. Flip this to `false` to
 * hide the "Cards" group and the card FX row again; the content stays here
 * either way, it is only filtered out on the way to the page.
 */
export const SHOW_CARD_PRICING = true;

/**
 * Strips card pricing from a table when it is hidden. Applied once per locale in
 * `localized.ts`, so English and Arabic can never drift apart — a structural
 * mismatch between the two is a test failure, not a visual bug someone spots
 * later on `/ar`.
 */
export function withCardVisibility(content: PricingContent): PricingContent {
  if (SHOW_CARD_PRICING) return content;
  return {
    ...content,
    groups: content.groups
      .filter((group) => !group.cards)
      .map((group) => ({ ...group, rows: group.rows.filter((row) => !row.cards) }))
      .filter((group) => group.rows.length > 0),
  };
}

export const pricing: Record<Audience, PricingContent> = { personal, business };
